import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  MenuItem,
  TextField,
  Grid,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Delete,
  Print,
  Receipt,
  CreditCard,
  AccountBalanceWallet,
  Money,
} from '@mui/icons-material';
import { appointmentService, patientService, printSettingsService, billingService, serviceRateService, authService } from '../../services/api';
import TopBar from '../../components/TopBar';

const defaultRow = () => ({ id: Date.now(), service: '', qty: 1, rate: '', amount: 0 });

const PAYMENT_MODES = [
  { value: 'Cash', label: 'Cash', icon: <Money fontSize="small" /> },
  { value: 'Card', label: 'Card', icon: <CreditCard fontSize="small" /> },
  { value: 'UPI',  label: 'UPI',  icon: <AccountBalanceWallet fontSize="small" /> },
];

const BillingPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rows, setRows] = useState([defaultRow()]);
  const [discount, setDiscount] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [billId, setBillId] = useState(null);
  const [shouldPrint, setShouldPrint] = useState(false);
  const [serviceRates, setServiceRates] = useState([]);

  useEffect(() => { loadData(); }, []);

  // Fire print only after React has re-rendered with the new billId
  useEffect(() => {
    if (shouldPrint) {
      window.print();
      setShouldPrint(false);
    }
  }, [shouldPrint]);

  const loadData = async () => {
    try {
      const [apptRes, patRes, psRes, srRes] = await Promise.all([
        appointmentService.getAllAppointments(),
        patientService.getAllPatients(),
        printSettingsService.getSettings(),
        serviceRateService.getAll(),
      ]);
      // Show OPD and IPD appointments that are Completed and billing not yet generated
      const billable = (apptRes.data || []).filter(
        (a) => (a.type === 'OPD' || a.type === 'IPD') && a.status === 'Completed' && a.billingStatus === 'Not Generated'
      );
      setAppointments(billable);
      setPatients(patRes.data || []);
      setPrintSettings(psRes.data || null);
      setServiceRates(srRes.data || []);
    } catch {
      setError('Failed to load data.');
    }
  };

  const getPatient = (patientId) => patients.find((p) => p.id === patientId);

  const handleAppointmentChange = (e) => {
    const apptId = e.target.value;
    setSelectedAppointmentId(apptId);
    const appt = appointments.find((a) => a.id === apptId);
    setSelectedAppointment(appt || null);
    setRows([defaultRow()]);
    setDiscount(0);
    setCgst(0);
    setSgst(0);
    setPaymentMode('Cash');
    setBillId(null);
    setSaveStatus(null);
  };

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        if (field === 'qty' || field === 'rate') {
          const qty  = parseFloat(field === 'qty'  ? value : r.qty)  || 0;
          const rate = parseFloat(field === 'rate' ? value : r.rate) || 0;
          updated.amount = qty * rate;
        }
        return updated;
      })
    );
  };

  // Called when user picks a service from the dropdown
  const selectService = (rowId, serviceRate) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        if (!serviceRate) return { ...r, service: '', qty: 1, rate: '', amount: 0 };
        const rate = serviceRate.rate;
        return { ...r, service: serviceRate.serviceName, qty: 1, rate: String(rate), amount: rate };
      })
    );
  };

  const addRow    = () => setRows((prev) => [...prev, defaultRow()]);
  const deleteRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const subtotal    = rows.reduce((sum, r) => sum + (r.amount || 0), 0);
  const discountAmt = (subtotal * (parseFloat(discount) || 0)) / 100;
  const afterDiscount = subtotal - discountAmt;
  const cgstAmt     = (afterDiscount * (parseFloat(cgst) || 0)) / 100;
  const sgstAmt     = (afterDiscount * (parseFloat(sgst) || 0)) / 100;
  const netAmount   = afterDiscount + cgstAmt + sgstAmt;

  const fmt = (n) => n.toFixed(2);
  const patient = selectedAppointment ? getPatient(selectedAppointment.patientId) : null;

  const handlePrintAndSave = async () => {
    setSaveStatus('saving');
    try {
      const payload = {
        appointmentId: selectedAppointment?.id || null,
        patientId: patient?.id,
        billedByUserId: user?.id,
        billDate: new Date().toISOString(),
        paymentMode,
        subtotal,
        discountPct: parseFloat(discount) || 0,
        discountAmount: discountAmt,
        cgstPct: parseFloat(cgst) || 0,
        cgstAmount: cgstAmt,
        sgstPct: parseFloat(sgst) || 0,
        sgstAmount: sgstAmt,
        netAmount,
        items: rows.map((r) => ({
          serviceName: r.service || '—',
          qty: parseFloat(r.qty) || 1,
          rate: parseFloat(r.rate) || 0,
          amount: r.amount || 0,
        })),
      };
      const res = await billingService.createBill(payload);
      setBillId(res.data?.id || null);  // update receipt with real BillId
      setSaveStatus('saved');
      setShouldPrint(true);             // useEffect fires window.print() after re-render
    } catch {
      setSaveStatus('error');
    }
  };

  return (
    <>
      {/* ── Print styles ── */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #billing-print-area, #billing-print-area * { visibility: visible; }
          #billing-print-area {
            position: absolute;
            left: 0; top: 0;
            width: 100%;
            padding: 24px;
            box-sizing: border-box;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* ── AppBar ── */}
      <TopBar title="Billing" onBack={() => navigate('/dashboard')} noPrint />

      {/* ── Screen content ── */}
      <Container maxWidth="md" sx={{ py: 4 }} className="no-print">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Appointment selector */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt /> Select Appointment
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Completed Appointments (OPD &amp; IPD)</InputLabel>
            <Select
              value={selectedAppointmentId}
              label="Completed Appointments (OPD & IPD)"
              onChange={handleAppointmentChange}
            >
              {appointments.length === 0 && (
                <MenuItem disabled value="">No completed appointments found</MenuItem>
              )}
              {appointments.map((a) => {
                const p = getPatient(a.patientId);
                const name = p ? `${p.firstName} ${p.lastName}` : `Patient #${a.patientId}`;
                return (
                  <MenuItem key={a.id} value={a.id}>
                    {name} — {a.type} @ {a.appointmentTime || ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {patient && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Patient:</strong> {patient.firstName} {patient.lastName}</Typography>
                  <Typography variant="body2"><strong>Age:</strong> {patient.age ?? '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Type:</strong> {selectedAppointment?.type}</Typography>
                  <Typography variant="body2"><strong>Date:</strong> {selectedAppointment?.appointmentDate?.split('T')[0]}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Line items */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Bill Items</Typography>
            <Button startIcon={<Add />} variant="outlined" size="small" onClick={addRow}>Add Item</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700, width: '40%' }}>Service / Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '12%' }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '18%' }} align="right">Rate (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '18%' }} align="right">Amount (₹)</TableCell>
                  <TableCell sx={{ width: '12%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={serviceRates}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : o.serviceName)}
                        value={serviceRates.find((s) => s.serviceName === row.service) || row.service}
                        onChange={(_, newVal) => {
                          if (newVal && typeof newVal === 'object') {
                            selectService(row.id, newVal);
                          } else {
                            updateRow(row.id, 'service', newVal || '');
                          }
                        }}
                        onInputChange={(_, newInput, reason) => {
                          if (reason === 'input') updateRow(row.id, 'service', newInput);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} variant="standard" placeholder="Select or type service" fullWidth />
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard" type="number"
                        inputProps={{ min: 1, style: { textAlign: 'right' } }}
                        sx={{ width: 60 }}
                        value={row.qty}
                        onChange={(e) => updateRow(row.id, 'qty', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard" type="number"
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        sx={{ width: 90 }}
                        value={row.rate}
                        onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">₹ {fmt(row.amount)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => deleteRow(row.id)} disabled={rows.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Summary + Payment Mode */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Bill Summary</Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Discount (%)" type="number" fullWidth size="small"
                inputProps={{ min: 0, max: 100 }}
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CGST (%)" type="number" fullWidth size="small"
                inputProps={{ min: 0 }}
                value={cgst}
                onChange={(e) => setCgst(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="SGST (%)" type="number" fullWidth size="small"
                inputProps={{ min: 0 }}
                value={sgst}
                onChange={(e) => setSgst(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
              />
            </Grid>
          </Grid>

          {/* Payment Mode */}
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Payment Mode</Typography>
            <ToggleButtonGroup
              value={paymentMode}
              exclusive
              onChange={(_, val) => { if (val) setPaymentMode(val); }}
              size="small"
            >
              {PAYMENT_MODES.map((m) => (
                <ToggleButton
                  key={m.value}
                  value={m.value}
                  sx={{
                    px: 3,
                    fontWeight: 600,
                    gap: 0.75,
                    '&.Mui-selected': {
                      bgcolor: '#1565c0',
                      color: '#fff',
                      '&:hover': { bgcolor: '#1565c0' },
                    },
                  }}
                >
                  {m.icon} {m.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ maxWidth: 340, ml: 'auto' }}>
            <SummaryRow label="Subtotal"                          value={`₹ ${fmt(subtotal)}`} />
            <SummaryRow label={`Discount (${discount || 0}%)`}   value={`- ₹ ${fmt(discountAmt)}`} />
            <SummaryRow label={`CGST (${cgst || 0}%)`}           value={`₹ ${fmt(cgstAmt)}`} />
            <SummaryRow label={`SGST (${sgst || 0}%)`}           value={`₹ ${fmt(sgstAmt)}`} />
            <Divider sx={{ my: 1 }} />
            <SummaryRow label="Net Amount" value={`₹ ${fmt(netAmount)}`} bold color="#1565c0" />
          </Box>
        </Paper>

        {saveStatus === 'saved' && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveStatus(null)}>
            Bill saved to database successfully.
          </Alert>
        )}
        {saveStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveStatus(null)}>
            Receipt printed but failed to save bill to database. Please try again.
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained" startIcon={<Print />}
            onClick={handlePrintAndSave}
            disabled={!selectedAppointment || saveStatus === 'saving'}
            size="large"
            sx={{ bgcolor: '#1565c0' }}
          >
            {saveStatus === 'saving' ? 'Saving…' : 'Print Receipt'}
          </Button>
        </Box>
      </Container>

      {/* ══════════ PRINT AREA ══════════ */}
      <div id="billing-print-area">
        <PrintReceipt
          printSettings={printSettings}
          patient={patient}
          appointment={selectedAppointment}
          rows={rows}
          subtotal={subtotal}
          discountPct={discount}
          discountAmt={discountAmt}
          cgstPct={cgst}
          cgstAmt={cgstAmt}
          sgstPct={sgst}
          sgstAmt={sgstAmt}
          netAmount={netAmount}
          paymentMode={paymentMode}
          billId={billId}
          fmt={fmt}
          user={user}
        />
      </div>
    </>
  );
};

/* ── Screen summary helper ── */
const SummaryRow = ({ label, value, bold, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, color: color || 'inherit' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, color: color || 'inherit' }}>{value}</Typography>
  </Box>
);

/* ── Print receipt ── */
const PrintReceipt = ({
  printSettings, patient, appointment, rows,
  subtotal, discountPct, discountAmt, cgstPct, cgstAmt, sgstPct, sgstAmt,
  netAmount, paymentMode, billId, fmt, user,
}) => {
  const now         = new Date();
  const billDateTime = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    + ', ' + now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  /* label-value table — labelAlign controls label column alignment */
  const LVTable = ({ rows: lvRows, labelAlign = 'right' }) => (
    <table style={{ borderCollapse: 'collapse' }}>
      <tbody>
        {lvRows.map(([label, value], i) => (
          <tr key={i}>
            <td style={{ textAlign: labelAlign, paddingRight: 6, whiteSpace: 'nowrap', fontWeight: 700, verticalAlign: 'top' }}>
              {label}
            </td>
            <td style={{ verticalAlign: 'top' }}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const patientRows = [
    ['Patient:', patient ? `${patient.firstName} ${patient.lastName}` : '—'],
    ...(patient?.age != null ? [['Age:', String(patient.age)]] : []),
    ...(patient?.gender     ? [['Gender:', patient.gender]] : []),
  ];

  const billInfoRows = [
    ...(billId != null ? [['Receipt No:', `#${billId}`]] : []),
    ['Bill Date:', billDateTime],
    ...(appointment ? [['Type:', appointment.type]] : []),
    ['Billed by:', `${user?.firstName || ''} ${user?.lastName || ''}`.trim()],
  ];

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', color: '#000' }}>
      {/* Hospital header */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        {printSettings?.hospitalLogo && (
          <img src={printSettings.hospitalLogo} alt="logo" style={{ maxHeight: 70, marginBottom: 6 }} />
        )}
        <div style={{ fontSize: 20, fontWeight: 700 }}>
          {printSettings?.hospitalName || 'Vedanta Hospital'}
        </div>
        {printSettings?.address && (
          <div style={{ fontSize: 11, color: '#444' }}>{printSettings.address}</div>
        )}
        {printSettings?.phone && (
          <div style={{ fontSize: 11, color: '#444' }}>Phone: {printSettings.phone}</div>
        )}
      </div>

      <hr style={{ borderTop: '2px solid #000', margin: '8px 0' }} />
      <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>
        PAYMENT RECEIPT
      </div>
      <hr style={{ borderTop: '1px dashed #888', margin: '8px 0' }} />

      {/* Patient info (left) + Bill info (right) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <LVTable rows={patientRows} labelAlign="left" />
        <LVTable rows={billInfoRows} labelAlign="left" />
      </div>

      <hr style={{ borderTop: '1px solid #000', margin: '8px 0' }} />

      {/* Items table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #000' }}>
            <th style={{ textAlign: 'left',  paddingBottom: 4, width: '4%'  }}>#</th>
            <th style={{ textAlign: 'left',  paddingBottom: 4               }}>Service / Description</th>
            <th style={{ textAlign: 'right', paddingBottom: 4, width: '8%'  }}>Qty</th>
            <th style={{ textAlign: 'right', paddingBottom: 4, width: '15%' }}>Rate (₹)</th>
            <th style={{ textAlign: 'right', paddingBottom: 4, width: '15%' }}>Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: '1px dashed #ccc' }}>
              <td style={{ padding: '4px 0' }}>{i + 1}</td>
              <td style={{ padding: '4px 0' }}>{r.service || '—'}</td>
              <td style={{ textAlign: 'right', padding: '4px 0' }}>{r.qty}</td>
              <td style={{ textAlign: 'right', padding: '4px 0' }}>{fmt(parseFloat(r.rate) || 0)}</td>
              <td style={{ textAlign: 'right', padding: '4px 0' }}>{fmt(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr style={{ borderTop: '1px solid #000', margin: '4px 0' }} />

      {/* Summary */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <PrintSummaryRow label="Subtotal"                        value={`₹ ${fmt(subtotal)}`} />
          <PrintSummaryRow label={`Discount (${discountPct||0}%)`} value={`- ₹ ${fmt(discountAmt)}`} />
          {parseFloat(cgstPct) > 0 && <PrintSummaryRow label={`CGST (${cgstPct}%)`} value={`₹ ${fmt(cgstAmt)}`} />}
          {parseFloat(sgstPct) > 0 && <PrintSummaryRow label={`SGST (${sgstPct}%)`} value={`₹ ${fmt(sgstAmt)}`} />}
          <tr><td colSpan="2"><hr style={{ borderTop: '1px solid #000', margin: '4px 0' }} /></td></tr>
          <PrintSummaryRow label="Net Amount" value={`₹ ${fmt(netAmount)}`} bold />
          <tr><td colSpan="2" style={{ paddingTop: 4 }}></td></tr>
          <PrintSummaryRow label="Payment Mode" value={paymentMode} />
        </tbody>
      </table>

      <hr style={{ borderTop: '2px solid #000', margin: '12px 0' }} />
      {printSettings?.footerText && (
        <div style={{ textAlign: 'center', fontSize: 11, color: '#444' }}>
          {printSettings.footerText}
        </div>
      )}
    </div>
  );
};

const PrintSummaryRow = ({ label, value, bold }) => (
  <tr>
    <td style={{ padding: '2px 0', fontWeight: bold ? 700 : 400 }}>{label}</td>
    <td style={{ textAlign: 'right', padding: '2px 0', fontWeight: bold ? 700 : 400 }}>{value}</td>
  </tr>
);

export default BillingPage;
